import { Button, type SxProps, type Theme } from '@mui/material';
import { createElement, ReactNode, useCallback } from 'react';
import useSnackbarStore, { SnackbarOptions } from 'src/store/snackbar';

const ACTION_TOAST_MS = 6000;

const undoButtonSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.custom.OV.sys.tertiary,
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: 1,
  minWidth: 0,
  padding: '0.3125rem 0.5625rem',
  borderRadius: '0.4375rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: `color-mix(in srgb, ${theme.palette.custom.OV.sys.tertiary} 14%, transparent)`,
  },
});

// an Alert sets its own colour per severity, so Undo takes it instead of the toast's tertiary
const alertUndoButtonSx: SxProps<Theme> = {
  color: 'inherit',
  fontWeight: 700,
  textTransform: 'none',
};

export interface ActionToastOptions {
  /** Runs where the change was made: a dialog that started it is already closed. */
  onUndo?: () => void;
  autoHide?: number;
}

export const useSnackbar = () => {
  const showSnackbar = useSnackbarStore.use.showSnackbar();
  const closeSnackbar = useSnackbarStore.use.closeSnackbar();

  const createUndoButton = useCallback(
    (onUndo: () => void, sx: SxProps<Theme>) =>
      createElement(
        Button,
        {
          size: 'small',
          sx,
          onClick: () => {
            closeSnackbar();
            onUndo();
          },
        },
        'Undo',
      ),
    [closeSnackbar],
  );

  const showSuccessAlert = useCallback(
    (
      message: ReactNode,
      {
        onUndo,
        ...options
      }: Omit<SnackbarOptions, 'severity'> & Pick<ActionToastOptions, 'onUndo'> = {},
    ) => {
      showSnackbar(message, {
        ...(onUndo && {
          autoHide: ACTION_TOAST_MS,
          action: createUndoButton(onUndo, alertUndoButtonSx),
        }),
        ...options,
        severity: 'success',
      });
    },
    [createUndoButton, showSnackbar],
  );

  const showErrorAlert = useCallback(
    (message: ReactNode, options?: Omit<SnackbarOptions, 'severity'>) => {
      showSnackbar(message, { ...options, severity: 'error' });
    },
    [showSnackbar],
  );

  const showInfoAlert = useCallback(
    (message: ReactNode, options?: Omit<SnackbarOptions, 'severity'>) => {
      showSnackbar(message, { ...options, severity: 'info' });
    },
    [showSnackbar],
  );

  /** A toast that names what just changed, with an optional Undo. */
  const showActionToast = useCallback(
    (message: ReactNode, { onUndo, autoHide = ACTION_TOAST_MS }: ActionToastOptions = {}) => {
      showSnackbar(message, {
        autoHide,
        placement: 'top',
        action: onUndo ? createUndoButton(onUndo, undoButtonSx) : undefined,
      });
    },
    [createUndoButton, showSnackbar],
  );

  return { showSuccessAlert, showErrorAlert, showInfoAlert, showActionToast };
};
